import { Trans } from '@lingui/react/macro';
import { UserCard } from '@ygtechnologies/bygr-frontend-component-library';
import React, { useState } from 'react';
import { getProjectCategoryPermissionTypes } from '@/services/item-categories';
import { getProfileImageUrl } from '@/services/profiles';
import { getUserAvatarThumbnailUrl } from '@/services/users';
import { LocalizedEnum, ProjectFeeSet, ProjectMemberDetails } from '@/types';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { useAsyncState } from '@/utils/hooks';
import UserPermissionsView from './UserPermissionsView';
import RemoveProjectMemberModal from './RemoveProjectMemberDialog/RemoveProjectMemberDialog';
import { getProjectFeeSets } from '@/services/fees';

interface CommonProps {
    onEditPermissionsClick(memberDetails: ProjectMemberDetails): void;
    onRemoveClick(memberDetails: ProjectMemberDetails): void;
    onInviteClick(memberDetails: ProjectMemberDetails): void;
}

type ProjectMemberPermissionsListProps = CommonProps & {
    members: ProjectMemberDetails[];
    projectId: number;
};

const ProjectMemberPermissionsList: React.FC<ProjectMemberPermissionsListProps> = ({ members, projectId, ...rest }) => {
    const [permissions] = useAsyncState(() => getProjectCategoryPermissionTypes(), [], null);
    const [projectFeeSets] = useAsyncState(() => getProjectFeeSets(projectId), [projectId], null);
    if (!projectFeeSets) {
        return null;
    }
    return (
        <div>
            {members
                .sort((a, b) => a.profileName?.localeCompare(b.profileName) || a.displayName?.localeCompare(b.displayName))
                .map((member) => (
                    <ProjectMemberPermissionsListItem
                        key={member.projectMemberId + ''}
                        member={member}
                        permissions={permissions}
                        projectFeeSets={projectFeeSets}
                        {...rest}
                    />
                ))}
        </div>
    );
};

type ProjectMemberPermissionsListItemProps = CommonProps & {
    member: ProjectMemberDetails;
    permissions: LocalizedEnum[] | null;
    projectFeeSets: ProjectFeeSet[];
};

const ProjectMemberPermissionsListItem: React.FC<ProjectMemberPermissionsListItemProps> = ({
    member,
    permissions,
    projectFeeSets,
    onEditPermissionsClick,
    onRemoveClick,
    onInviteClick,
}) => {
    const [removeUserModalOpen, setRemoveUserModalOpen] = useState(false);
    const handleRemove = async (memberDetails: ProjectMemberDetails) => {
        setRemoveUserModalOpen(true);
    };
    return (
        <>
            <UserCard
                data={{
                    userName: member.displayName,
                    userImageById: member.userId ? getUserAvatarThumbnailUrl(member.userId) : '',
                    profileImageById: getProfileImageUrl(member.profileId!),
                    profileName: member.profileName,
                    email: member.email,
                    phoneNumber: member.phoneNumber ?? <Trans>Not registered</Trans>,
                    isActive: member.status == 'Active',
                    isAdmin: member.isAdministrator,
                    permissions: (
                        <UserPermissionsView member={member} permissions={permissions} projectFeeSets={projectFeeSets} />
                    ),
                }}
                events={{
                    onEditPermissions: () => triggerHandler(onEditPermissionsClick, member),
                    onRemoveUser: () => triggerHandler(handleRemove, member),
                    onInviteUser: () => triggerHandler(onInviteClick, member),
                }}
            />
            {removeUserModalOpen && (
                <RemoveProjectMemberModal
                    toggle={() => setRemoveUserModalOpen(false)}
                    projectMember={member}
                    isOpen={removeUserModalOpen}
                    onRemoved={() => onRemoveClick(member)}
                />
            )}
        </>
    );
};

export default ProjectMemberPermissionsList;